'use client'

import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { usePrivy } from '@privy-io/react-auth'
import { CheckCircle2, Loader2, Sparkles, Wallet } from 'lucide-react'
import Image from 'next/image'
import { useSearchParams } from 'next/navigation'
import { forwardRef } from 'react'
import { useAccount } from 'wagmi'
import { useCardEligibility } from '../../hooks/useCardEligibility'
import { CardEligibilityResult } from '../../types/CardState'

interface CardLandingEligibilityProps {
  onEligibilityChecked: (result: CardEligibilityResult) => void
}

export const CardLandingEligibility = forwardRef<HTMLDivElement, CardLandingEligibilityProps>(
  ({ onEligibilityChecked }, ref) => {
    const { address } = useAccount()
    const { connectWallet } = usePrivy()
    const { checkEligibility, isLoading, error, reset } = useCardEligibility()
    const searchParams = useSearchParams()
    const referralCode = searchParams.get('ref')?.trim() || undefined

    const handleButtonClick = async () => {
      if (!address) {
        connectWallet()
        return
      }

      reset()

      try {
        const result = await checkEligibility(referralCode)
        onEligibilityChecked(result)
      } catch (err) {
        console.error('Error checking eligibility:', err)
      }
    }

    const getButtonText = () => {
      if (!address) return 'Connect Wallet'
      if (isLoading) return 'Checking...'
      return 'Sign In with HyperEVM'
    }

    const getButtonIcon = () => {
      if (isLoading) {
        return <Loader2 className="h-5 w-5 animate-spin" />
      }
      if (!address) {
        return <Wallet className="h-5 w-5" />
      }
      return <Image src="/tokens/hype.png" alt="HyperEVM" width={20} height={20} />
    }

    return (
      <section ref={ref} id="eligibility" className="py-12 lg:py-20">
        <div className="mb-12 text-center">
          <Typography variant="h2" className="text-3xl lg:text-4xl" gradient>
            Get Early Access
          </Typography>
          <Typography variant="p" className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            Connect your wallet to check your eligibility for the HypurrFi Card beta
          </Typography>
        </div>

        <div className="mx-auto max-w-xl">
          <Panel.Wrapper className="relative overflow-hidden">
            <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-gradient-yellow/20 via-transparent to-gradient-green/20" />

            <Panel.Content className="relative flex flex-col gap-6 p-8">
              <div className="flex flex-col gap-2">
                <Typography variant="prompt" className="text-muted-foreground">
                  Eligibility Status
                </Typography>
                <div
                  className={cn(
                    'flex items-center gap-3 rounded-lg border bg-background/50 p-4',
                    address ? 'border-primary/30' : 'border-border',
                  )}
                >
                  {address ? (
                    <>
                      <CheckCircle2 className="h-5 w-5 text-primary" />
                      <div className="flex-1">
                        <Typography variant="p" className="font-medium text-foreground">
                          Wallet Connected
                        </Typography>
                        <Typography variant="p" className="text-muted-foreground text-sm">
                          {address.slice(0, 6)}...{address.slice(-4)}
                        </Typography>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-dashed border-muted-foreground/50" />
                      <div className="flex-1">
                        <Typography variant="p" className="text-muted-foreground">
                          Connect your wallet to continue
                        </Typography>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <Button
                onClick={handleButtonClick}
                disabled={isLoading}
                size="lg"
                className={cn(
                  'w-full',
                  address && 'bg-gradient-to-r from-gradient-yellow to-gradient-green text-black hover:opacity-90',
                )}
                prefixIcon={getButtonIcon()}
              >
                {getButtonText()}
              </Button>

              <div className="flex items-center gap-2 rounded-lg bg-primary/5 p-3">
                <Sparkles className="h-4 w-4 flex-shrink-0 text-primary" />
                <Typography variant="p" className="text-muted-foreground text-sm">
                  Users with enough points will be granted direct access to the card beta.
                </Typography>
              </div>

              {error && (
                <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
                  <Typography variant="p" className="text-red-400 text-sm">
                    {error.message?.includes('User rejected') || error.message?.includes('rejected')
                      ? 'Signature was cancelled. Please try again to check your eligibility.'
                      : error.message || 'Failed to check eligibility. Please try again.'}
                  </Typography>
                </div>
              )}
            </Panel.Content>
          </Panel.Wrapper>
        </div>
      </section>
    )
  },
)

CardLandingEligibility.displayName = 'CardLandingEligibility'
